/**
 * Create Course Form Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ImageUpload, IMAGE_FORMATS } from '../ui/ImageUpload';
import { ChevronRight, Info, Globe, MapPin, Leaf, Wrench, GraduationCap, Sparkles } from 'lucide-react';
import axios from 'axios';
import { AVAILABLE_COUNTRIES } from '../../data/postalCodes';
import { 
  API_URL, 
  TARGET_AUDIENCE, 
  lookupPostalCodeWithGeo, 
  lookupCityToPostalCode 
} from './formUtils';

// Course Types with icons imported here
const COURSE_TYPES = [
  { key: 'workshop', label: { es: 'Taller', en: 'Workshop', it: 'Workshop' }, icon: Wrench, description: { es: 'Sesión intensiva corta', en: 'Short intensive session', it: 'Sessione intensiva breve' } },
  { key: 'retreat', label: { es: 'Retiro', en: 'Retreat', it: 'Ritiro' }, icon: Leaf, description: { es: 'Experiencia inmersiva de varios días', en: 'Multi-day immersive experience', it: 'Esperienza immersiva di più giorni' } },
  { key: 'training', label: { es: 'Formación', en: 'Training', it: 'Formazione' }, icon: GraduationCap, description: { es: 'Percurso certificado con varios módulos', en: 'Certified path with multiple modules', it: 'Percorso certificato con più moduli' } },
  { key: 'festival', label: { es: 'Festival', en: 'Festival', it: 'Festival' }, icon: Sparkles, description: { es: 'Evento con múltiples actividades y artistas', en: 'Event with multiple activities and artists', it: 'Evento con molteplici attività e artisti' } },
];

export function CreateCourseForm({ editMode = false, existingCourse = null }) {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders, user } = useAuthStore();
  const navigate = useNavigate();
  const language = user?.preferred_language || i18n.language || 'es';
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [schoolLocationLoaded, setSchoolLocationLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const [courseImage, setCourseImage] = useState(existingCourse?.images?.[0] || null);
  const [gallery, setGallery] = useState(existingCourse?.gallery || []);
  const [courseType, setCourseType] = useState(existingCourse?.course_type || null);
  const [title, setTitle] = useState(existingCourse?.title || { es: '', en: '', it: '' });
  const [description, setDescription] = useState(existingCourse?.description || { es: '', en: '', it: '' });
  const [price, setPrice] = useState(existingCourse?.price?.toString() || '');
  const [durationHours, setDurationHours] = useState(existingCourse?.duration_hours?.toString() || '');
  const [maxParticipants, setMaxParticipants] = useState(existingCourse?.max_participants?.toString() || '');
  const [isOnline, setIsOnline] = useState(existingCourse?.is_online || false);
  const [isResidential, setIsResidential] = useState(existingCourse?.is_residential || false);
  const [targetAudience, setTargetAudience] = useState(existingCourse?.target_audience || []);
  const [city, setCity] = useState(existingCourse?.location?.city || '');
  const [province, setProvince] = useState(existingCourse?.location?.province || '');
  const [area, setArea] = useState(existingCourse?.location?.area || '');
  const [country, setCountry] = useState(existingCourse?.location?.country || 'España');
  const [postalCode, setPostalCode] = useState(existingCourse?.location?.postal_code || '');
  const [address, setAddress] = useState(existingCourse?.location?.address || '');
  const [cityAutoFilled, setCityAutoFilled] = useState(!!existingCourse?.location?.city);
  const [coordinates, setCoordinates] = useState({ 
    lat: existingCourse?.location?.latitude || 0, 
    lng: existingCourse?.location?.longitude || 0 
  });
  const [geoError, setGeoError] = useState('');
  const [nearestCodes, setNearestCodes] = useState([]);
  const [multipleCities, setMultipleCities] = useState([]);
  const [showCitySelector, setShowCitySelector] = useState(false);
  const [startDate, setStartDate] = useState(
    existingCourse?.start_date ? existingCourse.start_date.split('T')[0] : ''
  );
  const [endDate, setEndDate] = useState(
    existingCourse?.end_date ? existingCourse.end_date.split('T')[0] : ''
  );
  const [skipInitialLookup, setSkipInitialLookup] = useState(editMode);

  // Load school location as default for new courses
  useEffect(() => {
    const loadSchoolLocation = async () => {
      if (editMode || schoolLocationLoaded) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/schools/me`, {
          headers: getAuthHeaders()
        });
        const school = response.data;
        
        if (school && school.location) {
          if (!city) setCity(school.location.city || '');
          if (!province) setProvince(school.location.province || '');
          if (!area) setArea(school.location.area || '');
          if (school.location.country) setCountry(school.location.country);
          if (!postalCode) setPostalCode(school.location.postal_code || '');
          if (!address) setAddress(school.location.address || '');
          if (school.location.latitude && school.location.longitude) {
            setCoordinates({
              lat: school.location.latitude,
              lng: school.location.longitude
            });
          }
          setCityAutoFilled(true);
          setSkipInitialLookup(true);
        }
        setSchoolLocationLoaded(true);
      } catch (error) {
        console.log('Could not load school location:', error);
        setSchoolLocationLoaded(true);
      }
    };
    
    loadSchoolLocation();
  }, [editMode, schoolLocationLoaded, getAuthHeaders]);

  // Gallery functions
  const addGalleryImage = (url) => {
    if (gallery.length < 10) {
      setGallery([...gallery, { url, caption: '' }]);
    }
  };
  const removeGalleryImage = (index) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };
  const updateGalleryCaption = (index, caption) => {
    const newGallery = [...gallery];
    newGallery[index] = { ...newGallery[index], caption };
    setGallery(newGallery);
  };

  // Auto-fill city and coordinates when postal code changes
  useEffect(() => {
    if (isOnline) return;
    
    if (skipInitialLookup) {
      setSkipInitialLookup(false);
      return;
    }
    
    const doLookup = async () => {
      const isSpain = ['españa', 'spain', 'spagna', 'espanha', 'spanien'].includes(country.toLowerCase().trim());
      
      if (isSpain && postalCode.length === 5) {
        setIsLookingUp(true);
        setGeoError('');
        setNearestCodes([]);
        setMultipleCities([]);
        setShowCitySelector(false);
        
        const result = await lookupPostalCodeWithGeo(postalCode, 'España');
        
        if (result.found) {
          // Check if this postal code has multiple cities
          if (result.hasMultipleCities && result.cities?.length > 1) {
            setMultipleCities(result.cities);
            setShowCitySelector(true);
            setProvince(result.province || '');
            setArea(result.area || '');
            setCity(''); // Clear city to force user selection
            setCityAutoFilled(false);
            setGeoError('');
          } else {
            setCity(result.city);
            setProvince(result.province || '');
            setArea(result.area || '');
            
            if (address.trim()) {
              try {
                const fullAddress = `${address}, ${result.city}, ${postalCode}, España`;
                const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`;
                const response = await fetch(nominatimUrl);
                const data = await response.json();
                
                if (data && data.length > 0) {
                  setCoordinates({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                } else {
                  setCoordinates({ lat: result.lat, lng: result.lng });
                }
              } catch (e) {
                setCoordinates({ lat: result.lat, lng: result.lng });
              }
            } else {
              setCoordinates({ lat: result.lat, lng: result.lng });
            }
            
            setCityAutoFilled(true);
            setGeoError('');
          }
        } else {
          setCityAutoFilled(false);
          setCoordinates({ lat: 0, lng: 0 });
          setNearestCodes(result.nearestCodes || []);
          setGeoError(language === 'es' 
            ? 'Código postal no encontrado. La geolocalización es obligatoria.'
            : 'Postal code not found. Geolocation is required.');
        }
        setIsLookingUp(false);
      } else {
        setCityAutoFilled(false);
        setGeoError('');
        setMultipleCities([]);
        setShowCitySelector(false);
      }
    };
    
    const timeoutId = setTimeout(doLookup, 500);
    return () => clearTimeout(timeoutId);
  }, [postalCode, country, language, isOnline, address]);

  // Handle city selection from multiple options
  const handleCitySelect = (selectedCity) => {
    setCity(selectedCity.city);
    setCoordinates({ lat: selectedCity.lat, lng: selectedCity.lng });
    setCityAutoFilled(true);
    setShowCitySelector(false);
    setGeoError('');
  };

  const toggleTargetAudience = (key) => {
    if (key === 'all') {
      setTargetAudience(targetAudience.includes('all') ? [] : ['all']);
    } else {
      let newAudience = targetAudience.filter(a => a !== 'all');
      if (newAudience.includes(key)) {
        newAudience = newAudience.filter(a => a !== key);
      } else {
        newAudience.push(key);
      }
      setTargetAudience(newAudience);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!courseImage) {
      setImageError(true);
      alert(language === 'es' 
        ? 'Por favor añade al menos una imagen del curso' 
        : 'Please add at least one course image');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setImageError(false);
    if (!courseType) { alert(language === 'es' ? 'Por favor selecciona un tipo de curso' : 'Please select a course type'); return; }
    if (!title[language]) { alert(language === 'es' ? 'Por favor introduce un título' : 'Please enter a title'); return; }
    if (!description[language]) { alert(language === 'es' ? 'Por favor introduce una descripción' : 'Please enter a description'); return; }
    if (!isOnline && (!postalCode.trim() || !city.trim())) {
      alert(language === 'es' ? 'Por favor introduce el código postal y la ciudad' : 'Please enter postal code and city');
      return;
    }
    
    if (!isOnline && coordinates.lat === 0 && coordinates.lng === 0) {
      alert(language === 'es' 
        ? 'La geolocalización es obligatoria. Por favor introduce un código postal válido.'
        : 'Geolocation is required. Please enter a valid postal code.');
      return;
    }
    
    setIsLoading(true);
    try {
      const payload = {
        course_type: courseType,
        title,
        description,
        price: price ? parseFloat(price) : null,
        currency: 'EUR',
        duration_hours: parseInt(durationHours) || 0,
        max_participants: parseInt(maxParticipants) || null,
        is_online: isOnline,
        is_residential: isResidential,
        target_audience: targetAudience.length > 0 ? targetAudience : ['all'],
        start_date: startDate || null,
        end_date: endDate || null,
        images: courseImage ? [courseImage] : [],
        gallery: gallery,
        location: isOnline ? { city: 'Online', country: '', province: '', area: '', address: '', postal_code: '', latitude: 0, longitude: 0 } : {
          city,
          province,
          area,
          country,
          address: address.trim(),
          postal_code: postalCode,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
      };
      
      if (editMode && existingCourse) {
        await axios.put(
          `${API_URL}/api/courses/${existingCourse.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        alert(language === 'es' 
          ? 'Cambios guardados. Pendientes de aprobación del administrador.'
          : 'Changes saved. Pending admin approval.');
      } else {
        await axios.post(
          `${API_URL}/api/courses`,
          payload,
          { headers: getAuthHeaders() }
        );
        alert(language === 'es' 
          ? 'Tu curso ha sido enviado para aprobación del administrador. Se te notificará cuando sea aprobado.'
          : 'Your course has been submitted for admin approval. You will be notified once it\'s approved.');
      }
      
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || (language === 'es' 
        ? (editMode ? 'Error al guardar los cambios' : 'Error al crear el curso')
        : (editMode ? 'Failed to save changes' : 'Failed to create course')));
    } finally {
      setIsLoading(false);
    }
  };

  const labels = {
    es: {
      back: 'Volver',
      title: editMode ? 'Editar Curso' : 'Crear Curso',
      coursePhoto: 'Foto del Curso',
      addPhoto: 'Añade una foto para tu curso',
      courseType: 'Tipo de Curso',
      courseTitle: 'Título',
      courseDescription: 'Descripción',
      enterTitle: 'Introduce el título del curso',
      enterDescription: 'Describe tu curso en detalle...',
      onlineCourse: 'Curso Online',
      residentialCourse: 'Curso Residencial',
      residentialInfo: 'Incluye alojamiento y comidas',
      courseFor: 'Curso Para',
      startDate: 'Fecha de Inicio',
      endDate: 'Fecha de Fin',
      price: 'Precio (EUR)',
      duration: 'Duración (horas)',
      maxParticipants: 'Máx. Participantes',
      location: 'Ubicación',
      locationHint: 'Precompilado con la dirección de tu escuela. Puedes modificarlo si el curso se realiza en otro lugar.',
      postalCode: 'Código Postal',
      city: 'Ciudad',
      cityAutoPlaceholder: 'Se completa automáticamente con el C.P.',
      selectCity: 'Selecciona tu ciudad',
      multipleCitiesFound: 'Este código postal corresponde a varias ciudades:',
      country: 'País',
      address: 'Dirección (opcional)',
      addressPlaceholder: 'Ej: Calle Mayor 15, 2º B',
      autoFilled: '(auto)',
      gallery: 'Galería de Fotos',
      galleryDesc: 'Añade hasta 10 fotos adicionales (opcional)',
      addToGallery: 'Añadir foto a la galería',
      captionPlaceholder: 'Añade un comentario (opcional)',
      infoText: editMode 
        ? 'Las modificaciones serán revisadas por un administrador antes de ser visibles.'
        : 'Tu curso será revisado por un administrador antes de ser visible para los usuarios.',
      submit: editMode ? 'Guardar Cambios' : 'Enviar para Aprobación',
      submitting: 'Guardando...',
    },
    en: {
      back: 'Back',
      title: editMode ? 'Edit Course' : 'Create Course',
      coursePhoto: 'Course Photo',
      addPhoto: 'Add a photo for your course',
      courseType: 'Course Type',
      courseTitle: 'Title',
      courseDescription: 'Description',
      enterTitle: 'Enter course title',
      enterDescription: 'Describe your course in detail...',
      onlineCourse: 'Online Course',
      residentialCourse: 'Residential Course',
      residentialInfo: 'Includes accommodation and meals',
      courseFor: 'Course For',
      startDate: 'Start Date',
      endDate: 'End Date',
      price: 'Price (EUR)',
      duration: 'Duration (hours)',
      maxParticipants: 'Max Participants',
      location: 'Location',
      locationHint: 'Pre-filled with your school address. You can change it if the course takes place elsewhere.',
      postalCode: 'Postal Code',
      city: 'City',
      cityAutoPlaceholder: 'Auto-filled from postal code',
      selectCity: 'Select your city',
      multipleCitiesFound: 'This postal code covers multiple cities:',
      country: 'Country',
      address: 'Address (optional)',
      addressPlaceholder: 'E.g.: 15 Main Street, Apt 2B',
      autoFilled: '(auto)',
      gallery: 'Photo Gallery',
      galleryDesc: 'Add up to 10 additional photos (optional)',
      addToGallery: 'Add photo to gallery',
      captionPlaceholder: 'Add a comment (optional)',
      infoText: editMode
        ? 'Changes will be reviewed by an administrator before becoming visible.'
        : 'Your course will be reviewed by an administrator before it becomes visible to users.',
      submit: editMode ? 'Save Changes' : 'Submit for Approval',
      submitting: 'Saving...',
    }
  };
  
  const l = labels[language] || labels.es;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="create-course-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {l.back}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{l.title}</h1>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Course Image */}
        <div className={imageError ? 'ring-2 ring-red-300 rounded-xl p-2 -m-2' : ''}>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-semibold text-primary">{l.coursePhoto}</label>
            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
              {language === 'es' ? 'Obligatorio' : 'Required'}
            </span>
          </div>
          <ImageUpload
            image={courseImage}
            onImageChange={(img) => { setCourseImage(img); setImageError(false); }}
            label=""
            aspectRatio={IMAGE_FORMATS.COURSE.aspectRatio}
            maxSize={IMAGE_FORMATS.COURSE.maxSize}
            shape="rectangle"
            placeholder={l.addPhoto}
            language={language}
            folder="courses/"
          />
          {imageError && (
            <p className="text-red-500 text-sm mt-1">
              {language === 'es' ? 'La imagen del curso es obligatoria' : 'Course image is required'}
            </p>
          )}
        </div>
        
        {/* Photo Gallery */}
        <div className="border-t pt-6">
          <label className="block text-sm font-semibold text-primary mb-2">{l.gallery}</label>
          <p className="text-sm text-secondary mb-4">{l.galleryDesc}</p>
          
          {gallery.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {gallery.map((item, index) => (
                <div key={item.url || `gallery-${index}`} className="relative group">
                  <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  <Input
                    placeholder={l.captionPlaceholder}
                    value={item.caption}
                    onChange={(e) => updateGalleryCaption(index, e.target.value)}
                    className="mt-2 text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          
          {gallery.length < 10 && (
            <ImageUpload
              image={null}
              onImageChange={(url) => addGalleryImage(url)}
              label=""
              aspectRatio={IMAGE_FORMATS.COURSE.aspectRatio}
              maxSize={IMAGE_FORMATS.COURSE.maxSize}
              shape="rectangle"
              placeholder={l.addToGallery}
              language={language}
              folder="courses/gallery/"
              showPreview={false}
            />
          )}
          
          {gallery.length >= 10 && (
            <p className="text-sm text-amber-600">
              {language === 'es' 
                ? 'Has alcanzado el máximo de 10 fotos en la galería.' 
                : 'You have reached the maximum of 10 photos in the gallery.'}
            </p>
          )}
        </div>
        
        {/* Course Type Selection */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.courseType} *</label>
          <div className="grid grid-cols-2 gap-4">
            {COURSE_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setCourseType(type.key)}
                className={`p-5 rounded-xl border-2 text-center transition-all ${
                  courseType === type.key 
                    ? 'border-gold bg-gold text-white' 
                    : 'border-gray-200 hover:border-gold'
                }`}
                style={courseType === type.key ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`course-type-${type.key}`}
              >
                <type.icon className={`w-8 h-8 mx-auto mb-2 ${courseType === type.key ? 'text-white' : 'text-gold'}`} style={courseType !== type.key ? { color: '#D4A574' } : {}} />
                <span className="font-semibold block">{type.label[language] || type.label.es}</span>
                <span className={`text-xs ${courseType === type.key ? 'text-white/80' : 'text-secondary'}`}>{type.description[language] || type.description.es}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Title & Description */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.courseTitle} ({language.toUpperCase()}) *</label>
          <Input placeholder={l.enterTitle} value={title[language]} onChange={(e) => setTitle({ ...title, [language]: e.target.value })} required data-testid="course-title" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.courseDescription} ({language.toUpperCase()}) *</label>
          <Textarea placeholder={l.enterDescription} value={description[language]} onChange={(e) => setDescription({ ...description, [language]: e.target.value })} rows={4} required data-testid="course-description" />
        </div>
        
        {/* Online Toggle */}
        <div 
          className="flex items-center justify-between p-4 bg-cream rounded-xl cursor-pointer"
          style={{ backgroundColor: '#F8F6F3' }}
          onClick={() => setIsOnline(!isOnline)}
          data-testid="online-toggle"
        >
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
            <span className="font-medium">{l.onlineCourse}</span>
          </div>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isOnline ? 'bg-gold' : 'bg-gray-300'}`} style={isOnline ? { backgroundColor: '#D4A574' } : {}}>
            <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isOnline ? 'translate-x-5' : ''}`} />
          </div>
        </div>
        
        {/* Residential Toggle (only if not online) */}
        {!isOnline && (
          <div 
            className="flex items-center justify-between p-4 bg-cream rounded-xl cursor-pointer"
            style={{ backgroundColor: '#F8F6F3' }}
            onClick={() => setIsResidential(!isResidential)}
            data-testid="residential-toggle"
          >
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
              <div>
                <span className="font-medium block">{l.residentialCourse}</span>
                <span className="text-xs text-secondary">{l.residentialInfo}</span>
              </div>
            </div>
            <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isResidential ? 'bg-gold' : 'bg-gray-300'}`} style={isResidential ? { backgroundColor: '#D4A574' } : {}}>
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isResidential ? 'translate-x-5' : ''}`} />
            </div>
          </div>
        )}
        
        {/* Target Audience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.courseFor}</label>
          <div className="flex flex-wrap gap-2">
            {TARGET_AUDIENCE.map((audience) => (
              <button
                key={audience.key}
                type="button"
                onClick={() => toggleTargetAudience(audience.key)}
                className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  targetAudience.includes(audience.key)
                    ? 'border-gold bg-gold text-white'
                    : 'border-gray-200 text-secondary hover:border-gold'
                }`}
                style={targetAudience.includes(audience.key) ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`audience-${audience.key}`}
              >
                {audience.label[language] || audience.label.es}
              </button>
            ))}
          </div>
        </div>
        
        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.startDate}</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.endDate}</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date" />
          </div>
        </div>
        
        {/* Price, Duration, Participants */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.price}</label>
            <Input type="number" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} min={0} data-testid="price" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.duration}</label>
            <Input type="number" placeholder="0" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} min={0} data-testid="duration" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.maxParticipants}</label>
            <Input type="number" placeholder="∞" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} min={1} data-testid="max-participants" />
          </div>
        </div>
        
        {/* Location (if not online) */}
        {!isOnline && (
          <>
            <h3 className="font-semibold text-primary pt-4 border-t">{l.location}</h3>
            {!editMode && schoolLocationLoaded && city && (
              <p className="text-sm text-secondary -mt-2 mb-2 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                {l.locationHint}
              </p>
            )}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{l.country} *</label>
              <Select value={country} onValueChange={(v) => setCountry(v)}>
                <SelectTrigger className="w-full" data-testid="country">
                  <SelectValue placeholder="Selecciona país" />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{l.postalCode} *</label>
                <Input 
                  placeholder="Ej: 28001" 
                  value={postalCode} 
                  onChange={(e) => setPostalCode(e.target.value)} 
                  maxLength={5} 
                  required 
                  data-testid="postal-code"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  {l.city} * {cityAutoFilled && <span className="text-green-600 text-xs">{l.autoFilled}</span>}
                </label>
                
                {/* City selector when multiple cities available */}
                {showCitySelector && multipleCities.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-600 font-medium">{l.multipleCitiesFound}</p>
                    <div className="grid gap-2">
                      {multipleCities.map((cityOption) => (
                        <button
                          key={`city-${cityOption.city}-${cityOption.province || ''}`}
                          type="button"
                          onClick={() => handleCitySelect(cityOption)}
                          className="text-left p-3 border rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors"
                          data-testid={`city-option-${cityOption.city}`}
                        >
                          <span className="font-medium">{cityOption.city}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Input 
                    placeholder={l.cityAutoPlaceholder || 'Compilato automaticamente dal CAP'}
                    value={city} 
                    readOnly
                    required 
                    className="bg-gray-100 cursor-not-allowed border-gray-200"
                    data-testid="city"
                  />
                )}
              </div>
            </div>
            
            {/* Address (optional) */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{l.address}</label>
              <Input 
                placeholder={l.addressPlaceholder}
                value={address} 
                onChange={(e) => setAddress(e.target.value)} 
                data-testid="address"
              />
            </div>
            
            {/* Geolocation Status */}
            {isLookingUp && (
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                {language === 'es' ? 'Buscando ubicación...' : 'Looking up location...'}
              </p>
            )}
            {cityAutoFilled && coordinates.lat !== 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> 
                {language === 'es' ? 'Geolocalización detectada automáticamente' : 'Geolocation detected automatically'}
              </p>
            )}
            {geoError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 font-medium">{geoError}</p>
                {nearestCodes.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-500">{language === 'es' ? 'Códigos postales cercanos:' : 'Nearest postal codes:'}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {nearestCodes.map(code => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setPostalCode(code)}
                          className="px-2 py-0.5 bg-white border border-red-300 rounded text-xs text-red-600 hover:bg-red-100"
                        >
                          {code} ({lookupPostalCode(code)?.city || 'Unknown'})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Info Box */}
        <div className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: '#E8F4FD' }}>
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">{l.infoText}</p>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading || (!isOnline && coordinates.lat === 0 && coordinates.lng === 0)} 
          className="w-full bg-gold text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed" 
          style={{ backgroundColor: '#D4A574' }} 
          data-testid="submit-course"
        >
          {isLoading ? l.submitting : l.submit}
        </Button>
      </form>
    </div>
  );
}
